import React, { useEffect, useMemo, useState } from "react";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import { get, ref } from "firebase/database";
import { useParams } from "react-router-dom";

import { database } from "../../firebase";
import RealTimeCard from "./RealTimeCard";
import { trailingAverage, useRealtimeSeries } from "./useRealtimeSeries";
import { baseChartOption, lineSeries, metricColor } from "../charts/chartTheme";
import { EM_DASH, describe, formatNumber } from "../../utils/format";

/* Firmware revisions disagree on these node names, so probe for whichever one
   this watch actually writes before subscribing. */
const DATA_KEYS = ["edaSkinConductance", "electrodermalActivity"];
const STATUS_KEYS = ["edaStatusDescription", "electrodermalActivityStatusDescription"];
const PROGRESS_KEYS = ["edaProgress", "electrodermalActivityProgress"];

const ElectrodermalActivityRealTime = () => {
  const { watchId } = useParams();
  const [paths, setPaths] = useState(null);

  useEffect(() => {
    if (!watchId) return undefined;

    let cancelled = false;
    const base = `Health/${watchId}/realTime/`;

    const resolve = async (candidates) => {
      for (const key of candidates) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const snapshot = await get(ref(database, `${base}${key}`));
          if (snapshot.exists()) return key;
        } catch (error) {
          console.error(`Failed to probe ${base}${key}`, error);
        }
      }
      return candidates[0];
    };

    (async () => {
      const [data, status, progress] = await Promise.all([
        resolve(DATA_KEYS),
        resolve(STATUS_KEYS),
        resolve(PROGRESS_KEYS),
      ]);
      if (cancelled) return;
      setPaths({
        data: `${base}${data}`,
        status: `${base}${status}`,
        progress: `${base}${progress}`,
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [watchId]);

  const { values, samples, status, progress, min, max } = useRealtimeSeries(
    watchId,
    paths
  );

  const stats = useMemo(() => describe(values, 2), [values]);

  const buildOption = () =>
    baseChartOption({
      categories: samples,
      series: [
        lineSeries({
          name: "Electrodermal activity",
          data: values,
          color: metricColor("eda"),
          smooth: 0.2,
          showSymbol: false,
        }),
      ],
      xName: "Sample",
      yName: "µS",
      yMin: min !== null ? Math.floor(min - 1) : undefined,
      yMax: max !== null ? Math.ceil(max + 1) : undefined,
      tooltipFormatter: (params) => {
        const point = params?.[0];
        if (!point) return "";
        return `Sample ${point.axisValue}<br/><strong>${formatNumber(
          point.value,
          2
        )} µS</strong>`;
      },
    });

  return (
    <RealTimeCard
      title="Live electrodermal activity"
      icon={<BoltOutlinedIcon />}
      accent={metricColor("eda")}
      exportName="electrodermalActivity"
      status={status}
      progress={progress}
      hasData={values.length > 0}
      waitingCopy="Skin conductance samples appear here as the watch streams them."
      deps={[values]}
      buildOption={buildOption}
      stats={[
        {
          label: "Latest",
          value: values.length ? formatNumber(values[values.length - 1], 2) : EM_DASH,
          unit: "µS",
          meta: "Most recent sample",
        },
        {
          label: "Avg (last 30s)",
          value: trailingAverage(values, 30, 2),
          unit: "µS",
          meta: "Rolling window",
        },
        { label: "Range", value: stats.range, unit: "µS", meta: "This capture" },
        { label: "Samples", value: values.length || EM_DASH, meta: "Received so far" },
      ]}
    />
  );
};

export default ElectrodermalActivityRealTime;
